import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import BulletinRichTextEditor from './BulletinRichTextEditor';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { presignAndUploadManualFile, isManualUploadConfiguredError } from '../api/manualUpload';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import type {
  ManualAttachmentPayload,
  ManualAttachmentRow,
  ManualDetailResponse,
  ManualGroupsTreeResponse,
} from '../constants/manual';

const MAX_FILES = 5;

const ManualEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const manualId = id && /^\d+$/.test(id) ? Number(id) : NaN;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [groups, setGroups] = useState<ManualGroupsTreeResponse['groups']>([]);
  const [groupId, setGroupId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [existing, setExisting] = useState<ManualAttachmentRow[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!Number.isInteger(manualId) || manualId < 1) {
      setLoadError('マニュアル ID が不正です');
      setLoading(false);
      return;
    }
    const run = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [detailRes, groupsRes] = await Promise.all([
          apiClient.get<ManualDetailResponse>(`/api/manual/manuals/${manualId}`),
          apiClient.get<ManualGroupsTreeResponse>('/api/manual/groups'),
        ]);
        const { manual } = detailRes.data;
        setGroups(groupsRes.data.groups);
        setGroupId(manual.group_id);
        setTitle(manual.title);
        setBody(manual.body);
        setExisting(manual.attachments);
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          setLoadError('マニュアルが見つかりません');
        } else {
          setLoadError('マニュアルの取得に失敗しました');
        }
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [manualId]);

  const totalCount = existing.length + newFiles.length;

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const nf = [...newFiles];
    for (let i = 0; i < list.length; i += 1) {
      if (existing.length + nf.length >= MAX_FILES) break;
      nf.push(list[i]);
    }
    setNewFiles(nf);
  };

  const submit = async () => {
    const t = title.trim();
    if (!t) {
      setError('タイトルを入力してください');
      return;
    }
    if (groupId === '' || !Number.isInteger(groupId)) {
      setError('グループを選択してください');
      return;
    }

    setError('');
    setSaving(true);
    try {
      const uploaded: ManualAttachmentPayload[] = [];
      for (const f of newFiles) {
        try {
          uploaded.push(await presignAndUploadManualFile(f));
        } catch (e: unknown) {
          if (isManualUploadConfiguredError(e)) {
            showSnackbar({
              message: 'ファイル添付は現在利用できません。添付を外すか本文のみ保存してください。',
              severity: 'error',
            });
            setSaving(false);
            return;
          }
          const msg =
            axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
              ? (e.response.data as { error?: string }).error
              : null;
          showSnackbar({ message: msg || 'ファイルのアップロードに失敗しました', severity: 'error' });
          setSaving(false);
          return;
        }
      }
      const kept = existing.map((a) => ({
        s3_key: a.s3_key,
        original_filename: a.original_filename,
        content_type: a.content_type,
        byte_size: a.byte_size,
      }));
      const attachments = [...kept, ...uploaded];

      await apiClient.patch(`/api/manual/manuals/${manualId}`, {
        group_id: groupId,
        title: t,
        body,
        attachments,
      });
      showSnackbar({ message: 'マニュアルを保存しました', severity: 'success' });
      navigate(`/manual/${manualId}`);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || 'マニュアルの更新に失敗しました', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          maxWidth: 960,
          width: '100%',
          mx: 'auto',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        マニュアルを編集
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <TextField
          label="タイトル"
          fullWidth
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth required sx={{ mb: 2, maxWidth: 400 }}>
          <InputLabel id="manual-edit-group-label">グループ</InputLabel>
          <Select
            labelId="manual-edit-group-label"
            label="グループ"
            value={groupId === '' ? '' : String(groupId)}
            onChange={(e) => setGroupId(Number(e.target.value))}
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <BulletinRichTextEditor label="本文" value={body} onChange={setBody} minRows={8} />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
          添付（最大 {MAX_FILES} 件）
        </Typography>
        <Button variant="outlined" component="label" size="small" sx={{ mb: 1 }} disabled={totalCount >= MAX_FILES}>
          ファイルを追加
          <input
            type="file"
            hidden
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {existing.map((a) => (
            <Chip
              key={a.id}
              label={a.original_filename}
              onDelete={() => setExisting(existing.filter((x) => x.id !== a.id))}
            />
          ))}
          {newFiles.map((f, i) => (
            <Chip
              key={`new-${f.name}-${i}`}
              label={f.name}
              onDelete={() => setNewFiles(newFiles.filter((_, j) => j !== i))}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={() => void submit()} disabled={saving}>
            保存する
          </Button>
          <Button variant="text" onClick={() => navigate(`/manual/${manualId}`)} disabled={saving}>
            キャンセル
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ManualEdit;
